import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import PageHeader from "../../components/pageHeader";
import MainContentLayout from "../../components/mainContentLayout";
import LoadingOverlay from "../../components/loadingOverlay";
import { API_BASE } from "../../config/apiConfig";
import OtherCoachTableRow from "./otherCoachTableRow";
import { FaTimes, FaSearch } from "react-icons/fa";
import { isAdminOnly } from "../../utils/roleHelpers";

export default function OtherCoachesListPage() {
  const token = sessionStorage.getItem("access_token");
  const [loading, setLoading] = useState(false);
  const user = useSelector((state) => state.user.user);
  // main listing
  const [coaches, setCoaches] = useState([]);

  // pagination (only for main non-search list)
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // search mode
  const [searching, setSearching] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searchResults, setSearchResults] = useState([]);

  // ----------------------------------------------------------------------------------
  // LOAD MAIN LIST (pagination)
  // ----------------------------------------------------------------------------------
  useEffect(() => {
    if (!searching) loadCoaches(page);
  }, [page, searching]);

  async function loadCoaches(pg) {
    setLoading(true);
    try {
      const url = new URL(`${API_BASE}/api/other-coaches-list`);
      url.searchParams.append("page", pg);
      url.searchParams.append("limit", 8);

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();

      if (data.success) {
        // sort alphabetically by fullname
        const sorted = [...(data.coaches || [])].sort((a, b) =>
          (a.fullName || "").localeCompare(b.fullName || "")
        );

        setCoaches(sorted);
        setTotalPages(data.totalPages || 1);
      }
    } finally {
      setLoading(false);
    }
  }

  // ----------------------------------------------------------------------------------
  // SEARCH
  // ----------------------------------------------------------------------------------
  async function handleSearch() {
    const q = searchText.trim();
    if (q.length < 2) return;

    setSearching(true);
    setLoading(true);

    try {
      const url = new URL(`${API_BASE}/api/search-coaches`);
      url.searchParams.append("query", q);

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();

      if (data.success) {
        const sorted = [...(data.results || [])].sort((a, b) =>
          (a.fullName || "").localeCompare(b.fullName || "")
        );
        setSearchResults(sorted);
      }
    } finally {
      setLoading(false);
    }
  }

  function clearSearch() {
    setSearching(false);
    setSearchText("");
    setSearchResults([]);
    setPage(1); // ensure reload of main list
    loadCoaches(1);
  }

  // ----------------------------------------------------------------------------------
  // UI
  // ----------------------------------------------------------------------------------
  return (
    <MainContentLayout>
      <LoadingOverlay show={loading} />
      <PageHeader title="All Coaches" backTo={isAdminOnly(user.role) ? "/dashboard" : "/coach-activities"} />

      <div className="space-y-6">

        {/* SEARCH BAR */}
        <div className="flex items-center gap-2 mt-2 mb-4">
          <div className="relative flex-grow">
            <input
              className="w-full border rounded-lg p-3 pr-10 placeholder:italic placeholder:text-gray-400"
              placeholder="Search by coach full name or email..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />

            {searchText.trim().length > 0 && (
              <button
                onClick={clearSearch}
                className="absolute right-10 top-1/2 -translate-y-1/2 text-gray-500"
              >
                <FaTimes />
              </button>
            )}
          </div>

          <button
            disabled={searchText.trim().length < 3}
            onClick={handleSearch}
            className="p-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <FaSearch />
          </button>
        </div>

        {/* TABLE */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <table className="min-w-full text-left">
            <thead className="bg-gray-100 border-b">
              <tr>
                <th className="px-4 py-2">Name</th>
                <th className="px-4 py-2 text-center"># Activities</th>
                <th className="px-4 py-2 text-center"></th>
              </tr>
            </thead>

            <tbody>
              {searching
                ? searchResults.map((c) => (
                    <OtherCoachTableRow key={c.id} coach={c} />
                  ))
                : coaches.map((c) => (
                    <OtherCoachTableRow key={c.id} coach={c} />
                  ))}
            </tbody>
          </table>

          {searching && searchResults.length === 0 && (
            <p className="text-center py-4 text-gray-500">
              No matching coaches found.
            </p>
          )}
        </div>

        {/* PAGINATION */}
        {!searching && totalPages > 1 && (
          <div className="flex justify-center items-center gap-8 mt-2">
            <button
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
              className="text-blue-600 hover:text-blue-800 disabled:opacity-40"
            >
              Prev
            </button>

            <span className="font-medium text-gray-700">
              Page {page} / {totalPages}
            </span>

            <button
              disabled={page === totalPages}
              onClick={() => setPage(page + 1)}
              className="text-blue-600 hover:text-blue-800 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </MainContentLayout>
  );
}
